import { Vector as VectorSource } from 'ol/source';
import { Vector as VectorLayer } from 'ol/layer';
import { Geometry, Point, LineString } from 'ol/geom';
import GeoJSON from 'ol/format/GeoJSON.js';
import { Circle, Fill, Text, Stroke, Style } from 'ol/style';
import { CoordinatesUtils } from '../utils/coordinates-utils';
import { processRouteForMap, ProcessRouteOptions, routeLineStyles } from '../utils/map-utils'
import { VoyageRoute, VoyageRoutingModel } from '../models/voyage-routing-model';
import { Feature } from 'ol';
import { VesselPositionModel } from '../models/vessel-position-model';

const _PROJECTION = (<any>window)["appenv"].projection;

export interface PlanningLayersColors
{
    defaultColor: string;
    uiRouteColors: string[];
    warningColor: string;
    avoidColor: string;
    waypointFillColor: string;
    parametricRollColor: string;
    passedColor: string;
}

export class PlanningLayers extends EventTarget
{
    constructor(colors?: Partial<PlanningLayersColors>)
    {
        super();
        this.setColors(colors || {});

        this._onRoutesChanged = this._onRoutesChanged.bind(this);
    }

    get colors(): PlanningLayersColors
    {
        return this._colors;
    }

    get layers(): VectorLayer<VectorSource<Geometry>>[]
    {
        return this._layers;
    }

    async initialize(): Promise<void>
    {
        // Routes
        this._routesVectorSource = new VectorSource();
        this._layers.push(new VectorLayer({source: this._routesVectorSource, visible: true}));

        VoyageRoutingModel!.addEventListener('routes-changed', this._onRoutesChanged);
    }

    setColors(colors: Partial<PlanningLayersColors>): void
    {
        this.colors.defaultColor = colors.defaultColor || this.colors.defaultColor;
        this.colors.uiRouteColors = colors.uiRouteColors || this.colors.uiRouteColors;
        this.colors.warningColor = colors.warningColor || this.colors.warningColor;
        this.colors.avoidColor = colors.avoidColor || this.colors.avoidColor;
        this.colors.parametricRollColor = colors.parametricRollColor || this.colors.parametricRollColor;
        this.colors.waypointFillColor = colors.waypointFillColor || this.colors.waypointFillColor;
        this.colors.passedColor = colors.passedColor || this.colors.passedColor;

        if (this._routesVectorSource)
        {
            this._onRoutesChanged();
        }
    }

    clearRoutes()
    {
        try {
            this._routesVectorSource!.clear();
        } catch (error) {
            console.warn(error);
        }
    }

    private _colors: PlanningLayersColors = {
        defaultColor: "black",
        uiRouteColors: [],
        warningColor: "yellow",
        avoidColor: "#ff6000",
        waypointFillColor: "black",
        parametricRollColor: "#E9B2B7",
        passedColor: "grey",
    };
    private _layers: VectorLayer<VectorSource<Geometry>>[] = [];
    private _routesVectorSource: VectorSource<Geometry>|null = null;

    private _plotRoute(route: VoyageRoute, uiRouteIndex: number)
    {
        const geoJSON = {
            type: 'FeatureCollection',
            features: route.route_geometry
        };

        let uiRouteColor = this.colors.defaultColor || "black";
        if (uiRouteIndex >= 0 && this.colors.uiRouteColors[uiRouteIndex])
        {
            uiRouteColor = this.colors.uiRouteColors[uiRouteIndex];
        }

        const wpHardStyle = new Style({
            image: new Circle({
                radius: 4,
                fill: new Fill({color: this.colors.waypointFillColor.trim()}),
                stroke: new Stroke({
                  color: "black", width: 1
                })
            })
        });

        const wpNavStyle = new Style({
            text: new Text({
                text: '+',
                font: 'bold 10px san-serif',
                fill: new Fill({color: this.colors.waypointFillColor.trim()}),
                stroke: new Stroke({color: 'black', width: 1}),
                scale: 1.8,
                offsetY: 3
            })
        });

        const wpPassedStyle = new Style({
            image: new Circle({
                radius: 4,
                fill: new Fill({ color: uiRouteColor.trim() })
            })
        });
        
        let features = (new GeoJSON()).readFeatures(geoJSON);
        const KmToNM = 1 / 1.852; // Km -> Nautical miles
        const plotFeatures = [];

        const processRouteOptions: ProcessRouteOptions  = {
            features: features,
            addWarnings: true
        }
        features = processRouteForMap(processRouteOptions);

        for (let feature of features) {
            if (feature.getGeometry() instanceof LineString) {
                // From lonlat to our projection
                feature.getGeometry()!.transform('EPSG:4326', _PROJECTION);
                // set line style and add warnings
                feature.setStyle((feature, res)=> routeLineStyles(<Feature<LineString>>feature, res, {...this.colors, uiRouteColor}));
                
                const coordinates = feature.get("originalFeature").getGeometry().getCoordinates();
                const dist = CoordinatesUtils.calcDistance(coordinates[0], coordinates[1]);
                const routeName = this._getRoutename(route.creationTime!, uiRouteIndex);
                let name = `Route: ${routeName} Distance (leg): ${(feature.get("leg_type") === "Rhumbline" ? dist.rl : dist.gc).toFixed(2)}nm`;
                if (route.route_info)
                {
                    name += ` Distance (total): ${(route.route_info.totals.non_eca.distance * KmToNM).toFixed(2)}nm`;
                }
                feature.set('name', name);
                plotFeatures.push(feature);

            } else if (feature.getGeometry() instanceof Point) {
                feature.getGeometry()!.transform('EPSG:4326', _PROJECTION);
                if (feature.get("isPassed"))
                {
                    feature.setStyle(wpPassedStyle);
                }
                else
                {
                    feature.setStyle(feature.get("type") === "Hard" ? wpHardStyle : wpNavStyle);
                }
                plotFeatures.push(feature);
            }
        }

        this._routesVectorSource!.addFeatures(plotFeatures);
    }

    private _onRoutesChanged()
    {
        this.clearRoutes();
        const uiRoutes = VoyageRoutingModel!.uiRoutes;
        VoyageRoutingModel!.routes.forEach(route => {
            this._plotRoute(route, uiRoutes.findIndex( uiRoute => uiRoute.route === route));
        });
    }

    private _getRoutename(creationTime: string, uiRouteIndex: number): string
    {
        let name = creationTime;
        if (uiRouteIndex >= 0)
        {
            name = `Route ${uiRouteIndex+1}`;
        }
        return name;
    }
};
